import { useState, useEffect, useMemo } from 'react';
import { serialApi, SerialItem } from '@/api/serial.api';

interface Props {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemName: string;
  selectedSerials: string[];
  onApply: (serials: string[]) => void;
  preloadedSerials?: string[];
}

interface SelectableSerial {
  id?: string;
  serial_number: string;
  imei?: string;
  vendor_serial_number?: string;
  warehouse_name?: string;
}

export default function SerialSelectModal({
  open,
  onClose,
  itemId,
  itemName,
  selectedSerials: initialSelected,
  onApply,
  preloadedSerials,
}: Props) {
  const [serials, setSerials] = useState<SelectableSerial[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set(initialSelected));
  const [manualMode, setManualMode] = useState(false);
  const [manualInput, setManualInput] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(initialSelected));
    setSearch('');
    setManualMode(false);

    async function fetchSerials() {
      try {
        setLoading(true);
        const res = await serialApi.getAvailable(itemId);
        if (res.success && Array.isArray(res.data)) {
          // If preloadedSerials passed, also ensure they are in the list
          const fetched = res.data;
          if (preloadedSerials && preloadedSerials.length > 0) {
            const existingNums = new Set(fetched.map((s) => s.serial_number));
            const extra: SelectableSerial[] = preloadedSerials
              .filter((num) => !existingNums.has(num))
              .map((num) => ({
                id: num,
                serial_number: num,
              }));
            setSerials([...extra, ...fetched]);
          } else {
            setSerials(fetched);
          }
        } else if (preloadedSerials && preloadedSerials.length > 0) {
          setSerials(
            preloadedSerials.map((num) => ({
              id: num,
              serial_number: num,
            }))
          );
        }
      } catch (err) {
        console.error('Failed to load available serials:', err);
        if (preloadedSerials && preloadedSerials.length > 0) {
          setSerials(
            preloadedSerials.map((num) => ({
              id: num,
              serial_number: num,
            }))
          );
        }
      } finally {
        setLoading(false);
      }
    }

    fetchSerials();
  }, [open, itemId, initialSelected, preloadedSerials]);

  const filteredSerials = useMemo(() => {
    if (!search.trim()) return serials;
    const q = search.toLowerCase();
    return serials.filter(
      (s) =>
        s.serial_number.toLowerCase().includes(q) ||
        (s.imei && s.imei.toLowerCase().includes(q)) ||
        (s.vendor_serial_number && s.vendor_serial_number.toLowerCase().includes(q))
    );
  }, [serials, search]);

  const toggleSelect = (sn: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) {
        next.delete(sn);
      } else {
        next.add(sn);
      }
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      filteredSerials.forEach((s) => next.add(s.serial_number));
      return next;
    });
  };

  const handleDeselectAll = () => {
    setSelected(new Set());
  };

  const handleApplyManual = () => {
    const parsed = manualInput
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (parsed.length > 0) {
      setSelected((prev) => {
        const next = new Set(prev);
        parsed.forEach((sn) => next.add(sn));
        return next;
      });
      setManualInput('');
      setManualMode(false);
    }
  };

  const handleConfirm = () => {
    onApply(Array.from(selected));
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600">
              <i className="ri-barcode-line text-lg" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 text-sm">Select Serial Numbers</h3>
              <p className="text-xs text-slate-500 font-medium truncate max-w-md">{itemName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100 space-y-3 bg-white">
          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search serial number, IMEI..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs"
                >
                  <i className="ri-close-circle-fill" />
                </button>
              )}
            </div>

            <button
              onClick={() => setManualMode(!manualMode)}
              className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={manualMode ? 'ri-list-check' : 'ri-edit-line'} />
              {manualMode ? 'View List' : 'Paste Serials'}
            </button>
          </div>

          {manualMode ? (
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
              <p className="text-xs text-slate-600 font-medium">
                Paste or enter multiple serial numbers (separated by comma or newline):
              </p>
              <textarea
                rows={3}
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="SN1001&#10;SN1002&#10;SN1003..."
                className="w-full p-2 border border-slate-200 rounded-md text-xs font-mono outline-none focus:border-indigo-500 bg-white"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setManualMode(false)}
                  className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyManual}
                  disabled={!manualInput.trim()}
                  className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-medium disabled:opacity-40"
                >
                  Add to Selection
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between text-xs text-slate-600">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSelectAllFiltered}
                  disabled={filteredSerials.length === 0}
                  className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-medium rounded border border-indigo-200 transition-colors disabled:opacity-40 cursor-pointer"
                >
                  <i className="ri-checkbox-line mr-1" />
                  Select All ({filteredSerials.length})
                </button>
                {selected.size > 0 && (
                  <button
                    onClick={handleDeselectAll}
                    className="px-2.5 py-1 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                  >
                    Deselect All
                  </button>
                )}
              </div>
              <div className="font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-full">
                {selected.size} selected
              </div>
            </div>
          )}
        </div>

        {/* Serials List */}
        <div className="flex-1 overflow-y-auto p-4 max-h-[400px]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <i className="ri-loader-4-line text-2xl animate-spin mb-2 text-indigo-600" />
              <p className="text-xs">Loading available serial numbers...</p>
            </div>
          ) : filteredSerials.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <i className="ri-barcode-line text-3xl mb-2 opacity-50 block" />
              <p className="text-sm font-medium text-slate-600">No serial numbers found</p>
              <p className="text-xs text-slate-400 mt-1">
                {search
                  ? 'No matching serial numbers for your search query.'
                  : 'No available serial numbers found in stock for this item.'}
              </p>
              <button
                onClick={() => setManualMode(true)}
                className="mt-3 px-3 py-1.5 text-xs text-indigo-600 hover:text-indigo-700 font-medium border border-indigo-200 rounded-lg inline-flex items-center gap-1"
              >
                <i className="ri-add-line" /> Enter Serials Manually
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {filteredSerials.map((s) => {
                const isChecked = selected.has(s.serial_number);
                return (
                  <label
                    key={s.id || s.serial_number}
                    className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                      isChecked
                        ? 'border-indigo-500 bg-indigo-50/50 shadow-sm'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50/60'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelect(s.serial_number)}
                      className="w-4 h-4 mt-0.5 accent-indigo-600 cursor-pointer shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs font-semibold text-slate-800 truncate">
                        {s.serial_number}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                        {s.imei && <span>IMEI: {s.imei}</span>}
                        {s.warehouse_name && <span>{s.warehouse_name}</span>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <span className="text-xs text-slate-500">
            {selected.size} serial barcode label{selected.size !== 1 ? 's' : ''} will be generated
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs text-slate-600 hover:text-slate-800 font-medium cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors disabled:opacity-40 cursor-pointer flex items-center gap-1.5"
            >
              <i className="ri-check-line" />
              Apply {selected.size} Serial{selected.size !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
